import {
    ArrowRight,
    Check,
    ChevronDown,
    CircleX,
    Copy,
    Ellipsis,
    LoaderCircle,
    ThumbsDown,
    ThumbsUp,
} from 'lucide-react';
import {
    getMessageSteps,
    getMessageText,
    parseFollowUpIds,
    useAssistantContext,
} from './AssistantProvider';
import { Fragment, useEffect, useState } from 'react';
import { Badge } from '@/ui/design-system/components/ui/badge';
import { Link } from 'react-router-dom';
import { mixpanelTrack } from 'utils/app/configureMixpanel';
import toast from 'react-hot-toast';
import FollowUps, { REPORT_FOLLOW_UP } from './FollowUps';
import CreateReportChip from './CreateReportChip';
import {
    GOOGLE_METADATA_BY_KEY,
    POPULARITY_METADATA_BY_KEY,
    TIKTOK_METADATA_BY_KEY,
} from 'utils/metrics';
import ErrorBoundary from '@/ui/containers/ErrorBoundary';
import { selectInlineVisuals } from './visuals';
import InlineVisualError from './visuals/primitives/InlineVisualError';
import {
    formatDataSource,
    formatGeoLabel,
} from './visuals/lib/breadcrumbFormat';

/**
 * Strip <sources> and <follow_ups> blocks from assistant message text so they
 * don't render as raw text. Both partial and complete forms are handled so
 * tags don't flash visible mid-stream.
 */
function stripTags(text) {
    return text
        .replace(/<sources>.*?<\/sources>/s, '') // complete sources block
        .replace(/<sources>.*$/s, '') // partial sources block during streaming
        .replace(/<follow_ups>.*?<\/follow_ups>/s, '') // complete follow_ups block
        .replace(/<follow_ups>.*$/s, '') // partial follow_ups block during streaming
        .trim();
}

const formatAffinityGroup = (groups) => {
    if (!groups || !Array.isArray(groups) || groups.length === 0) return '';
    return `(${groups.join(', ')})`;
};

const formatLifeCycle = (lifeCycle) => {
    if (!lifeCycle || !Array.isArray(lifeCycle) || lifeCycle.length === 0)
        return '';
    return `${lifeCycle.join(', ')} trends`;
};

const formatPrice = (price) => {
    if (!price || !Array.isArray(price) || price.length === 0) return '';
    return `(${price.map((p) => String(p).toLowerCase()).join(', ')})`;
};

const METADATA_BY_SOURCE = {
    pop_index: POPULARITY_METADATA_BY_KEY,
    tiktok: TIKTOK_METADATA_BY_KEY,
    google: GOOGLE_METADATA_BY_KEY,
};

const PRESET_LABELS_BY_SOURCE = {
    tiktok: {
        trending_this_week: 'Trending This Week',
        trending_this_month: 'Trending This Month',
        organic_trends: 'Organic Trends',
        high_engagement: 'High Engagement',
        new: 'New',
    },
};

const formatOrderBy = (orderBy, dataSource = 'pop_index') => {
    if (!orderBy) return '';
    const meta = (METADATA_BY_SOURCE[dataSource] ?? POPULARITY_METADATA_BY_KEY)[
        orderBy
    ];
    return meta?.tableHeadColumnLabel ? `by ${meta.tableHeadColumnLabel}` : '';
};

// Sentence-case fallback for a relationship without an explicit entry below
// (e.g. 'sounds' → 'Sounds', 'related_categories' → 'Related categories'). Lets a
// new tool render a sensible substep label with no RELATIONSHIP_NOUN/PHRASING edit.
const defaultNoun = (rel) => {
    const spaced = String(rel || 'entity data').replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Optional overrides for the in-flight substep noun; unlisted relationships fall back
// to defaultNoun(rel). Only add an entry when the derived label reads wrong.
const RELATIONSHIP_NOUN = {
    self: 'Overview',
    cat5s: 'Subcategories',
    brands: 'Brands',
    related_categories: 'Related categories',
    hashtags: 'Hashtags',
    creators: 'Creators',
    posts: 'Posts',
};

// Optional overrides for the resolved-entity phrasing; unlisted relationships fall
// back to `${name} ${rel}` (underscores spaced). Add an entry only for irregular cases.
const RELATIONSHIP_PHRASING = {
    self: (name) => <>{name} overview</>,
    cat5s: (name) => <>{name} subcategories</>,
    brands: (name) => <>{name} brands</>,
    related_categories: (name) => <>{name} related categories</>,
    hashtags: (name) => <>{name} hashtags</>,
    creators: (name) => <>{name} creators</>,
    posts: (name) => <>{name} posts</>,
};

const Capitalized = ({ children }) => (
    <span className="capitalize">{children}</span>
);

// Parts may be strings or JSX (a CSS-capitalized name), so interleave rather
// than String.join.
const joinDots = (...parts) =>
    parts.filter(Boolean).map((part, i) => (
        <Fragment key={i}>
            {i > 0 && ' · '}
            {part}
        </Fragment>
    ));

const Decorated = ({ head, presetLabel, order }) => (
    <>
        {presetLabel ? joinDots(head, presetLabel) : head}
        {order && ` ${order}`}
    </>
);

// TikTok hashtags: surface the tactical preset (the filter+sort bundle the
// user picked) and the resolved sort. The preset's sort is decided server-side,
// so prefer the backend-resolved values from `output.result` over the input.
const decorateHashtagsHead = (head, input, output) => (
    <Decorated
        head={head}
        presetLabel={
            output?.result?.applied_preset ||
            PRESET_LABELS_BY_SOURCE.tiktok[input.preset] ||
            ''
        }
        order={formatOrderBy(
            output?.result?.order_by ?? input.order_by,
            'tiktok',
        )}
    />
);

const decoratePostsHead = (head, input, output) => (
    <Decorated
        head={head}
        order={formatOrderBy(
            output?.result?.order_by ?? input.order_by,
            'tiktok',
        )}
    />
);

// Per-relationship title decorators; add a key here for a new shape rather than
// branching inside decorateTitleHead.
const HEAD_DECORATORS = {
    hashtags: decorateHashtagsHead,
    posts: decoratePostsHead,
    related_posts: decoratePostsHead,
};

const decoratePopIndexHead = (head, input) => {
    const rel = input.entity_relationship ?? 'self';
    const lifeCycle = formatLifeCycle(input.life_cycle);
    const price = formatPrice(input.price);
    const affinity = formatAffinityGroup(input.affinity_group);
    const orderBy =
        rel !== 'self' ? formatOrderBy(input.order_by, input.data_source) : '';
    return (
        <>
            {head}
            {lifeCycle && ` (${lifeCycle})`}
            {price && ` ${price}`}
            {affinity && ` ${affinity}`}
            {orderBy && ` ${orderBy}`}
        </>
    );
};

const decorateTitleHead = (head, input, output) => {
    const rel = input.entity_relationship ?? 'self';
    const decorator = HEAD_DECORATORS[rel];
    return decorator
        ? decorator(head, input, output)
        : decoratePopIndexHead(head, input);
};

const describeEntityDataInFlight = (input, output) => {
    const rel = input.entity_relationship ?? 'self';
    const head = decorateTitleHead(
        RELATIONSHIP_NOUN[rel] || defaultNoun(rel),
        input,
        output,
    );
    return joinDots(
        formatGeoLabel(input.geo),
        formatDataSource(input.data_source),
        head,
    );
};

const describeEntityDataResolved = (input, entity, output) => {
    if (!entity?.name) return describeEntityDataInFlight(input, output);
    const name = <Capitalized>{entity.name}</Capitalized>;
    const rel = input.entity_relationship ?? 'self';
    const phrasing =
        RELATIONSHIP_PHRASING[rel] ||
        ((n) => (
            <>
                {n} {String(rel).replace(/_/g, ' ')}
            </>
        ));
    const head = decorateTitleHead(phrasing(name), input, output);
    return joinDots(
        formatGeoLabel(input.geo),
        formatDataSource(input.data_source),
        head,
    );
};

const ToolCallGetEntity = ({ state, input, output, toolCallId }) => {
    if (state === 'input-streaming') {
        return 'Entity search';
    }

    if (state === 'input-available') {
        const type = input.entity_type ? (
            <Capitalized>{input.entity_type}</Capitalized>
        ) : (
            'Entity'
        );
        return joinDots(
            formatGeoLabel(input.geo),
            <>
                {type} "{input.name}"
            </>,
        );
    }

    if (
        state === 'output-available' &&
        output?.success &&
        output?.result?.entity
    ) {
        const entity = output.result.entity;
        const type = entity.type ? (
            <Capitalized>{entity.type}</Capitalized>
        ) : (
            'Entity'
        );
        return joinDots(
            formatGeoLabel(input.geo),
            <>
                {type} "<Capitalized>{entity.name}</Capitalized>"
            </>,
        );
    }

    if (state === 'output-available') {
        const type = input.entity_type ? (
            <Capitalized>{input.entity_type}</Capitalized>
        ) : (
            'Entity'
        );
        return joinDots(
            formatGeoLabel(input.geo),
            <>
                No match: {type} "{input.name}"
            </>,
        );
    }

    console.error(
        `Unknown tool call state: ${state} for toolCallId: ${toolCallId}`,
        { state, input, output },
    );
    return null;
};

const ToolCallGetEntityData = ({ state, input, output, toolCallId }) => {
    if (state === 'input-streaming') {
        return 'Entity data';
    }

    if (state === 'input-available') {
        return describeEntityDataInFlight(input);
    }

    if (state === 'output-available' && output?.success) {
        // `_entity` moved from result._entity to top-level; fall back for chats
        // persisted before that change so reopening them keeps the resolved name.
        const entity = output?._entity ?? output?.result?._entity;
        return describeEntityDataResolved(input, entity, output);
    }

    if (state === 'output-available') {
        const desc = describeEntityDataInFlight(input);
        const err =
            typeof output?.error === 'string' && output.error.trim()
                ? output.error
                : null;
        return err ? (
            <>
                {desc} — {err}
            </>
        ) : (
            desc
        );
    }

    console.error(
        `Unknown tool call state: ${state} for toolCallId: ${toolCallId}`,
        { state, input, output },
    );
    return null;
};

const ToolCallKnowledge = () => 'Documentation';

// The tool's error strings are written for the model (recovery instructions),
// so the substep shows a short human label instead of output.error.
const ToolCallCreateReport = ({ state, output }) => {
    if (state === 'output-available' && output?.success) {
        return output.title
            ? `Report created: ${output.title}`
            : 'Report created';
    }
    if (state === 'output-available') return 'Couldn’t create a report';
    return 'Creating report…';
};

const substepOutcome = (substep) => {
    if (substep.state !== 'output-available') return null;
    if (substep.type === 'tool-get_entity') {
        return substep.output?.success && substep.output?.result?.entity
            ? 'success'
            : 'failure';
    }
    if (substep.type === 'tool-get_entity_data') {
        return substep.output?.success ? 'success' : 'failure';
    }
    return substep.output?.success !== false ? 'success' : 'failure';
};

const TOOL_CALL_COMPONENTS = {
    'tool-get_entity': ToolCallGetEntity,
    'tool-get_entity_data': ToolCallGetEntityData,
    'tool-get_knowledge': ToolCallKnowledge,
    'tool-create_report': ToolCallCreateReport,
};

const SubstepLabel = ({ substep }) => {
    const Component = TOOL_CALL_COMPONENTS[substep.type];
    if (!Component) return null;
    return (
        <Component
            state={substep.state}
            input={substep.input}
            output={substep.output}
            toolCallId={substep.toolCallId}
        />
    );
};

const SubstepIcon = ({ outcome, state }) => {
    if (outcome === 'success') {
        return <Check size={16} className="shrink-0 text-green-500" />;
    }
    if (outcome === 'failure') {
        return <CircleX size={16} className="shrink-0 text-muted-foreground" />;
    }
    if (state === 'input-available' || state === 'input-streaming') {
        return (
            <LoaderCircle
                size={16}
                className="shrink-0 text-muted-foreground animate-spin"
            />
        );
    }
    return (
        <Ellipsis
            size={16}
            className="shrink-0 text-muted-foreground opacity-50"
        />
    );
};

const SubstepRow = ({ substep }) => (
    <div className="bg-card border border-border rounded-[10px] shadow-xs h-8 px-2.5 flex items-center gap-2">
        <SubstepIcon outcome={substepOutcome(substep)} state={substep.state} />
        <span className="text-xs font-medium text-muted-foreground flex-1 truncate">
            <SubstepLabel substep={substep} />
        </span>
    </div>
);

export const AssistantSteps = ({ steps, isLoading, hasText }) => {
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        if (hasText) {
            setExpanded(false);
        }
    }, [hasText]);

    const allSubsteps = steps?.flat() ?? [];
    const totalSteps = allSubsteps.length;
    if (totalSteps === 0 && !isLoading) return null;

    return (
        <div>
            <button
                onClick={() => setExpanded((v) => !v)}
                className="bg-muted flex items-center justify-between w-full px-2.5 py-1.5 rounded-[10px] h-8 border-none cursor-pointer"
            >
                <div className="flex items-center gap-2">
                    <ChevronDown
                        size={14}
                        className={`text-muted-foreground transition-transform ${!expanded ? '-rotate-90' : ''}`}
                    />
                    <span className="text-xs font-medium text-muted-foreground">
                        Process
                    </span>
                    {totalSteps > 0 && (
                        <Badge
                            variant="outline"
                            className="text-[10px] py-0 px-2 h-5"
                        >
                            {totalSteps} {totalSteps === 1 ? 'step' : 'steps'}
                        </Badge>
                    )}
                </div>
                {!isLoading ? (
                    <Badge className="bg-[#dcfce7] text-[#16a34a] border-transparent text-[10px] py-0 px-2 h-5 gap-1">
                        <Check size={12} /> Done
                    </Badge>
                ) : (
                    <Badge className="bg-muted-foreground/10 text-muted-foreground border-transparent text-[10px] py-0 px-2 h-5 gap-1">
                        <LoaderCircle size={12} className="animate-spin" />{' '}
                        Working
                    </Badge>
                )}
            </button>
            {expanded && (
                <div className="pl-4">
                    <div className="border-l border-border pl-3 pt-3 flex flex-col gap-2">
                        {allSubsteps.map((substep, i) => (
                            <SubstepRow key={i} substep={substep} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const ACTION_BTN_CLASS =
    'flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground cursor-pointer transition-all active:scale-95';

const FEEDBACK_VARIANTS = {
    up: {
        Icon: ThumbsUp,
        ariaLabel: 'Thumbs up',
        activeClass: 'text-green-600',
        idleClass: 'hover:bg-green-50 hover:text-green-600',
    },
    down: {
        Icon: ThumbsDown,
        ariaLabel: 'Thumbs down',
        activeClass: 'text-red-600',
        idleClass: 'hover:bg-red-50 hover:text-red-600',
    },
};

const FeedbackButton = ({ action, feedback, onSelect }) => {
    const { Icon, ariaLabel, activeClass, idleClass } =
        FEEDBACK_VARIANTS[action];
    const stateClass =
        feedback === action
            ? activeClass
            : feedback
              ? 'opacity-50 cursor-not-allowed'
              : idleClass;
    return (
        <button
            onClick={() => onSelect(action)}
            disabled={!!feedback}
            className={`${ACTION_BTN_CLASS} ${stateClass}`}
            aria-label={ariaLabel}
        >
            <Icon size={16} />
        </button>
    );
};

const MessageActions = ({
    text,
    messageId,
    isLastAssistantMessage,
    isLoading,
}) => {
    const [copied, setCopied] = useState(false);
    const [feedback, setFeedback] = useState(null); // 'up' | 'down' | null

    if (isLoading) return null;

    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        mixpanelTrack('AI Assistant Response Copied', {
            message_id: messageId,
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const handleFeedback = (action) => {
        if (feedback) return;
        setFeedback(action);
        mixpanelTrack('AI Assistant Feedback', { action, messageId });
        toast.success(
            action === 'up'
                ? 'Thanks for the feedback!'
                : "Thanks, we'll work on improving.",
        );
    };

    const visibilityClass = isLastAssistantMessage
        ? 'opacity-100'
        : 'opacity-0 group-hover:opacity-100 transition-opacity';

    return (
        <div className={`flex items-center gap-2 mt-3 ${visibilityClass}`}>
            <button
                onClick={handleCopy}
                className={`${ACTION_BTN_CLASS} hover:bg-muted hover:text-foreground`}
                aria-label="Copy message"
            >
                {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
            <FeedbackButton
                action="up"
                feedback={feedback}
                onSelect={handleFeedback}
            />
            <FeedbackButton
                action="down"
                feedback={feedback}
                onSelect={handleFeedback}
            />
        </div>
    );
};

/**
 * Resolve <follow_ups> IDs against the most recent get_entity_data tool result
 * on this message. Preserves the LLM's emitted order; drops IDs that don't
 * appear in the tool's follow_ups list (defensive against hallucinated IDs).
 *
 * The report offer is prepended ahead of the early returns: it has no catalog
 * entry, and has to appear on a turn that made no tool call of its own.
 */
export function getFollowUpEntries(message, rawText, offerReport = false) {
    const reportEntry = offerReport ? [REPORT_FOLLOW_UP] : [];
    const ids = parseFollowUpIds(rawText);
    if (ids.length === 0) return reportEntry;

    const entityDataParts = message.parts.filter(
        (p) =>
            p.type === 'tool-get_entity_data' &&
            p.state === 'output-available' &&
            p.output?.success,
    );
    if (entityDataParts.length === 0) return reportEntry;

    const latest = entityDataParts[entityDataParts.length - 1];
    const followUps = latest.output?.result?.follow_ups;
    if (!Array.isArray(followUps) || followUps.length === 0) return reportEntry;

    const byId = new Map(followUps.map((f) => [f.id, f]));
    return [...reportEntry, ...ids.map((id) => byId.get(id)).filter(Boolean)];
}

/**
 * A pending create_report counts as already produced: the stream settles while
 * the client executes the tool, so the offer would otherwise reappear
 * mid-create and invite a duplicate.
 */
export function canOfferReport(messages = []) {
    let hasData = false;
    for (const message of messages) {
        for (const part of message.parts ?? []) {
            if (
                part.type === 'tool-create_report' &&
                (part.state === 'input-streaming' ||
                    part.state === 'input-available' ||
                    (part.state === 'output-available' && part.output?.success))
            ) {
                return false;
            }
            if (
                part.type === 'tool-get_entity_data' &&
                part.state === 'output-available' &&
                part.output?.success
            ) {
                hasData = true;
            }
        }
    }
    return hasData;
}

const AssistantMessage = ({ message, isLoading, isLastAssistantMessage }) => {
    const { messages, isLoading: isChatBusy } = useAssistantContext();
    const steps = getMessageSteps(message);
    const rawText = getMessageText(message);
    const text = stripTags(rawText);
    const navigationPart = message.parts.find(
        (p) => p.type === 'data-navigation',
    );
    const createdReportParts = message.parts.filter(
        (p) =>
            p.type === 'tool-create_report' &&
            p.state === 'output-available' &&
            p.output?.success,
    );
    // Chat-level flag, not the `isLoading` prop: that prop goes false as soon
    // as a user turn is appended after this message, leaving suggestions
    // clickable through the submitted phase, where a second click sends twice.
    const followUpEntries =
        isLastAssistantMessage && !isChatBusy
            ? getFollowUpEntries(message, rawText, canOfferReport(messages))
            : [];
    const [inlineVisual] = selectInlineVisuals(message.parts, navigationPart);

    // A turn stopped before its first token keeps only a metadata part.
    const hasBody =
        text ||
        steps.some((step) => step.length > 0) ||
        navigationPart?.data.url ||
        inlineVisual ||
        createdReportParts.length > 0 ||
        followUpEntries.length > 0;
    if (!hasBody && !isLoading) return null;

    return (
        <div
            className="group self-start w-full flex flex-col gap-5"
            data-message-role="assistant"
            data-message-id={message.id}
        >
            <AssistantSteps
                steps={steps}
                isLoading={isLoading && !text}
                hasText={!!text}
            />
            {/* url-less navigation parts (no dashboard page exists) only anchor
                the inline visual — no banner, nowhere to navigate. */}
            {navigationPart && navigationPart.data.url && (
                <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-white border border-[#16A34A]/60">
                    <ArrowRight size={12} className="text-green-600 shrink-0" />
                    <span className="text-xs text-muted-foreground">
                        Navigated to{' '}
                        <Link
                            to={navigationPart.data.url}
                            className="text-green-600 font-medium capitalize hover:underline"
                        >
                            {navigationPart.data.label}
                        </Link>
                    </span>
                </div>
            )}
            {text && (
                <div
                    className={`text-sm leading-relaxed whitespace-pre-wrap text-[#1B1A2E] line-height-[162.5%] max-w-[90%] md:max-w-[80%]`}
                >
                    {text}
                    {isLoading && <span className="animate-pulse">|</span>}
                </div>
            )}
            {inlineVisual && (
                <ErrorBoundary FallbackComponent={InlineVisualError}>
                    <inlineVisual.Component {...inlineVisual.props} />
                </ErrorBoundary>
            )}
            {createdReportParts.map((p) => (
                <CreateReportChip
                    key={p.toolCallId ?? p.output.report_id}
                    output={p.output}
                />
            ))}
            {followUpEntries.length > 0 && (
                <FollowUps entries={followUpEntries} />
            )}
            {text && (
                <MessageActions
                    text={text}
                    messageId={message.id}
                    isLastAssistantMessage={isLastAssistantMessage}
                    isLoading={isLoading}
                />
            )}
        </div>
    );
};

export default AssistantMessage;
